import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '../../../Components/Atoms/Button';
import Typography from '../../../Components/Atoms/Typography';
import LoadingScreen from '../../../Components/Atoms/LoadingScreen';
import { AuthContext, invitationLoadedFor, requestRefs, Restaurant } from '../../../Contexts/AuthContext';
import InvitationLayout from '../../Components/InvitationLayout';
import { RequestStatus, requestStatusFor } from '../../../utils/requestStatus';

const DASHBOARD_URL = process.env.REACT_APP_DASHBOARD_URL || 'https://app.gastronaut.ai';

type Docs = {
  membership?: boolean;
  profileHasRestaurant?: boolean;
  request?: { status?: string } | null;
};

const TEXT: Record<RequestStatus, { title: string; body: string }> = {
  pending: {
    title: 'Approval pending',
    body: 'Your request was sent to {{restaurant}}. A responsible person has to approve your access.',
  },
  approved: {
    title: 'Approved',
    body: 'Your access to {{restaurant}} is being set up …',
  },
  ready: {
    title: 'Your access is ready',
    body: 'Sign in to the dashboard with {{email}} to open {{restaurant}}.',
  },
  declined: {
    title: 'Request not approved',
    body: '{{restaurant}} did not approve this request. Please contact the restaurant if you think this is a mistake.',
  },
  missing: {
    title: 'No open request',
    body: 'There is no request (any more) for this account at {{restaurant}}.',
  },
};

// Follows the request while this page is open: approval alone does not open
// the dashboard yet, only the membership and the profile entry do.
const PendingInvit = () => {
  const { invitationId = '' } = useParams();
  const { t } = useTranslation('helpersAndErrors');
  const { invitation, init, authReady, user, signOutUser, requestStatus } = useContext(AuthContext);
  const navigate = useNavigate();
  const [docs, setdocs] = useState<Docs>({});
  const [checking, setchecking] = useState(false);
  const [checkedStatus, setcheckedStatus] = useState<RequestStatus | null>(null);
  const [unreadable, setunreadable] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  // The account and restaurant the shown status belongs to.
  const watched = useRef('');
  const restaurantId = invitation.restaurantId;

  // A reload lands here directly: load the invitation again.
  useEffect(() => {
    if (invitation.id !== invitationId) init(invitationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitationId]);

  useEffect(() => {
    if (invitationLoadedFor(invitation, invitationId) && invitation.invalid) {
      navigate(`/${invitationId}/error`, { replace: true });
    }
  }, [invitation, invitationId, navigate]);

  useEffect(() => {
    watched.current = user && restaurantId ? `${user.uid}/${restaurantId}` : '';
    setdocs({});
    setcheckedStatus(null);
    setunreadable(false);
    if (!user || !restaurantId) return;
    const refs = requestRefs(user.uid, restaurantId);
    const failed = (error: Error) => {
      console.error(error);
      setunreadable(true);
    };
    const unsubscribes = [
      refs.membership.onSnapshot((doc) => setdocs((d) => ({ ...d, membership: doc.exists })), failed),
      refs.profile.onSnapshot(
        (doc) =>
          setdocs((d) => ({
            ...d,
            profileHasRestaurant: !!doc.data()?.restaurants?.some((r: Restaurant) => r.id === restaurantId),
          })),
        failed
      ),
      refs.request.onSnapshot((doc) => setdocs((d) => ({ ...d, request: doc.exists ? doc.data() : null })), failed),
    ];
    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }, [user, restaurantId]);

  const loaded = docs.membership !== undefined && docs.profileHasRestaurant !== undefined && docs.request !== undefined;
  // Without live updates a manual check still shows where the request stands.
  const status: RequestStatus | null = loaded
    ? requestStatusFor({
        membership: !!docs.membership,
        profileHasRestaurant: !!docs.profileHasRestaurant,
        request: docs.request,
      })
    : checkedStatus;

  useEffect(() => {
    if (status) heading.current?.focus();
  }, [status]);

  const checkNow = async () => {
    if (!user || !restaurantId) return;
    const target = `${user.uid}/${restaurantId}`;
    setchecking(true);
    try {
      const checked = await requestStatus(user.uid, restaurantId);
      if (watched.current === target) setcheckedStatus(checked);
    } catch (error) {
      console.error(error);
      setunreadable(true);
    } finally {
      setchecking(false);
    }
  };

  if (!invitationLoadedFor(invitation, invitationId) || !authReady) return <LoadingScreen loading />;

  if (!user) {
    return (
      <InvitationLayout>
        <Typography variant='h3' className='invitation-title' translation='helpersAndErrors'>
          Sign in to see your request
        </Typography>
        <div className='invitation-actions'>
          <Button variant='primary' fullWidth translation='helpersAndErrors' onClick={() => navigate(`/${invitationId}`)}>
            Sign in
          </Button>
        </div>
      </InvitationLayout>
    );
  }

  if (!status && unreadable) {
    return (
      <InvitationLayout>
        <Typography variant='h3' className='invitation-title' translation='helpersAndErrors'>
          Status not available
        </Typography>
        <div className='invitation-banner tone-error' role='alert'>
          <Typography variant='text-3' translation='helpersAndErrors'>
            The status of your request could not be loaded. Please check your connection and try again.
          </Typography>
        </div>
        <div className='invitation-actions'>
          <Button variant='primary' fullWidth loading={checking} disabled={checking} translation='helpersAndErrors' onClick={checkNow}>
            Check status
          </Button>
          <Button variant='transparent' fullWidth translation='helpersAndErrors' onClick={signOutUser}>
            Use another account
          </Button>
        </div>
      </InvitationLayout>
    );
  }

  if (!status) return <LoadingScreen loading />;

  const values = { restaurant: invitation.restaurantName ?? '', email: user.email ?? '' };

  return (
    <InvitationLayout>
      <h1 className='invitation-title' ref={heading} tabIndex={-1} style={{ outline: 'none' }}>
        <Typography variant='h3' translation={null}>
          {t(TEXT[status].title)}
        </Typography>
      </h1>
      <div role='status' aria-live='polite'>
        <Typography variant='text-2' color='subdued' className='invitation-lead' translation={null}>
          {t(TEXT[status].body, values)}
        </Typography>
      </div>

      {(status === 'pending' || status === 'approved') && (
        <div className='invitation-banner'>
          <Typography variant='text-3' translation='helpersAndErrors'>
            You can leave this page open or come back later through the same link.
          </Typography>
        </div>
      )}

      <div className='invitation-actions'>
        {status === 'ready' && restaurantId && (
          <Button variant='primary' fullWidth translation='helpersAndErrors' href={`${DASHBOARD_URL}/${restaurantId}`}>
            Open restaurant
          </Button>
        )}
        {(status === 'pending' || status === 'approved') && (
          <Button
            variant='outlined'
            fullWidth
            loading={checking}
            disabled={checking}
            startIcon={checking ? () => <CircularProgress size={14} /> : null}
            translation='helpersAndErrors'
            onClick={checkNow}
          >
            Check status
          </Button>
        )}
        {status === 'missing' && (
          <Button
            variant='primary'
            fullWidth
            translation='common'
            onClick={() => navigate(`/${invitationId}/sendRequest`)}
          >
            Send request
          </Button>
        )}
        {checkedStatus && checkedStatus === status && !checking && (
          <Typography variant='text-3' color='subdued' translation='helpersAndErrors'>
            Checked just now. Nothing has changed yet.
          </Typography>
        )}
        <Button variant='transparent' fullWidth translation='helpersAndErrors' onClick={signOutUser}>
          Use another account
        </Button>
      </div>
    </InvitationLayout>
  );
};

export default PendingInvit;
